import { validate as uuidValidate } from "uuid";

/**
 * The link INTO the "Shifts & Punches" change log, focused on one shift.
 *
 * A support user who spots an odd punch in the day view opens that shift's raw punches and asks the next
 * question: *why does it look like this?* This module is the contract that carries them there — the
 * query-param names, the builder the punch dialog uses, and the reader the log parses on arrival — so
 * neither side can drift from the other.
 */

/** Query-param names. One object so a rename is a single edit on both sides of the link. */
export const CHANGELOG_PARAM = {
	/** Which sub-tab to open ("shifts" for this log; anything else falls back to the default tab). */
	tab: "tab",
	/** Preselects the employee filter, so leaving focus mode shows their whole history. */
	nurseId: "nurse_id",
	/** The nurse_in_shift_actual id to focus on. Its presence IS focus mode. */
	shiftId: "shift_id",
} as const;

/** The `tab` value that opens the Shifts & Punches sub-tab. */
export const SHIFTS_TAB_PARAM_VALUE = "shifts";

/** Where a shift's sync change log lives — department-scoped, like every admin page. */
export const buildShiftChangelogPath = ({ departmentId, nurseId, shiftId }: { departmentId: string; nurseId: number; shiftId: string }): string => {
	const params = new URLSearchParams({
		[CHANGELOG_PARAM.tab]: SHIFTS_TAB_PARAM_VALUE,
		[CHANGELOG_PARAM.nurseId]: String(nurseId),
		[CHANGELOG_PARAM.shiftId]: shiftId,
	});
	return `/${departmentId}/employee-changelog?${params.toString()}`;
};

export interface ShiftChangelogFocus {
	/** Undefined when absent or not a positive integer — a junk param must not select "nurse NaN". */
	nurseId?: number;
	/** Undefined when absent or not a UUID. A well-formed id that simply doesn't exist still 404s. */
	shiftId?: string;
}

/** Read the focus out of a location's search params. Pure — safe to call on every render. */
export const readShiftChangelogFocus = (search: URLSearchParams): ShiftChangelogFocus => {
	const rawNurseId = search.get(CHANGELOG_PARAM.nurseId);
	const nurseId = rawNurseId === null ? Number.NaN : Number(rawNurseId);
	const shiftId = search.get(CHANGELOG_PARAM.shiftId)?.trim();
	return {
		nurseId: Number.isInteger(nurseId) && nurseId > 0 ? nurseId : undefined,
		// Shape-checked here, not just non-empty: the route types the path param as a UUID, so a
		// hand-mangled `?shift_id=abc` would be rejected by FastAPI with a raw 422 before the handler's
		// own 404 could answer. Rejecting it up front lands the reader on the normal employee picker
		// instead of an error the page has no copy for.
		shiftId: shiftId && uuidValidate(shiftId) ? shiftId : undefined,
	};
};
